import { useEffect, useState } from "react";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Loader2,
    ArrowRight,
    Landmark,
    Scale,
    Gavel,
    BookMarked,
    Wrench,
    MoreHorizontal,
    Inbox,
} from "lucide-react";

const TYPE_META = {
    istituzionale: { label: "Fonti istituzionali", icon: Landmark },
    normativa: { label: "Normativa", icon: Scale },
    sentenza: { label: "Sentenze", icon: Gavel },
    guida_ministeriale: { label: "Guide ministeriali", icon: BookMarked },
    documentazione_tecnica: { label: "Documentazione tecnica", icon: Wrench },
    altro: { label: "Altro", icon: MoreHorizontal },
};

export default function Resources() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        API.get("/public/resources").then(({ data }) => setItems(data))
            .catch(e => setError(apiError(e))).finally(() => setLoading(false));
    }, []);

    const isSingle = items.length === 1;

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
            <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold inline-flex items-center gap-1.5">
                <Landmark className="size-3.5" /> NowGestioni s.r.l.
            </div>
            <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight" data-testid="resources-title">
                Servizi offerti da NowGestioni s.r.l.
            </h1>
            <p className="mt-4 text-lg lg:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Tutti i nostri servizi
            </p>

            {error && <Alert variant="destructive" className="mt-6"><AlertDescription>{error}</AlertDescription></Alert>}

            <div className="mt-14 lg:mt-16">
                {loading ? (
                    <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                ) : items.length === 0 ? (
                    <Card className="border-dashed border-2 bg-muted/30" data-testid="resources-empty">
                        <CardContent className="py-14 text-center">
                            <Inbox className="size-10 mx-auto text-muted-foreground/40" />
                            <p className="mt-3 text-muted-foreground">Nessuna risorsa ancora pubblicata.</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div
                        className={
                            isSingle
                                ? "grid grid-cols-1 sm:max-w-md gap-5 lg:gap-6"
                                : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
                        }
                        data-testid="resources-grid"
                    >
                        {items.map(r => {
                            const meta = TYPE_META[r.resource_type] || TYPE_META.altro;
                            const Icon = meta.icon;
                            return (
                                <a
                                    key={r.id}
                                    href={r.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`resource-${r.id}`}
                                    className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 lg:p-7 transition-all duration-300 hover:border-primary/40 hover:shadow-[0_16px_40px_-15px_rgba(48,131,201,0.35)] hover:-translate-y-1 focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/40"
                                >
                                    <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5 group-hover:bg-accent/15 group-hover:text-accent transition-colors duration-300">
                                        <Icon className="size-6" strokeWidth={2} />
                                    </div>

                                    <div className="flex-1">
                                        <h3 className="font-serif text-xl font-semibold text-slate-900 leading-tight group-hover:text-primary transition-colors duration-300">
                                            {r.title}
                                        </h3>
                                        {r.description && (
                                            <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                                                {r.description}
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-accent group-hover:gap-3 transition-all duration-300">
                                        <span>Scopri il servizio</span>
                                        <ArrowRight className="size-4" strokeWidth={2.5} />
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
